import React, { useState, useEffect } from 'react';
import { getPillar } from '../pillars';

/**
 * TeachBack - Reflection and teaching component
 *
 * After completing a level, learner explains the concept in their own words.
 * This is vulnerable thinking - must feel safe, private, not judged.
 *
 * Design principles:
 * - Private by default (local storage only)
 * - No character limits that feel restrictive
 * - Any language accepted without detection or correction
 * - Never required, but strongly encouraged
 * - Framed as "for you, not for us"
 */
function TeachBack({ levelId, conceptName, prompt, pillarKey, onComplete, onSkip }) {
  const [teachBackText, setTeachBackText] = useState('');
  const [isSaved, setIsSaved] = useState(false);

  // Load existing teach-back from local storage
  useEffect(() => {
    const storageKey = `sifiso-teachback-${levelId}`;
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      setTeachBackText(saved);
      setIsSaved(true);
    }
  }, [levelId]);

  const handleSave = () => {
    if (teachBackText.trim().length === 0) {
      return;
    }

    // Save to local storage only
    const storageKey = `sifiso-teachback-${levelId}`;
    localStorage.setItem(storageKey, teachBackText);
    setIsSaved(true);

    // Proceed to next level
    if (onComplete) {
      onComplete(teachBackText);
    }
  };

  const handleSkip = () => {
    if (onSkip) {
      onSkip();
    }
  };

  const pillar = pillarKey ? getPillar(pillarKey) : null;

  return (
    <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
      {/* Header - calm, not urgent */}
      <div className="mb-4">
        <h3 className="text-xl font-semibold text-slate-200 mb-2">
          Teach What You Learned
        </h3>
        <p className="text-slate-400 text-sm leading-relaxed">
          This is for you, not for us. Writing helps you understand more deeply.
        </p>
      </div>

      {/* Pillar indicator - subtle reminder of what capacity is being built */}
      {pillar && (
        <div className="mb-4 flex items-center gap-2 text-sm">
          <span className="text-lg">{pillar.icon}</span>
          <span className={`${pillar.textColor} font-medium`}>
            You're practicing {pillar.shortName} Literacy
          </span>
        </div>
      )}

      {/* The prompt - specific to the concept */}
      <div className="mb-4 p-4 bg-slate-900/50 rounded-lg border-l-4 border-blue-500">
        <p className="text-slate-300 leading-relaxed">
          {prompt || `How would you explain ${conceptName} to someone younger than you?`}
        </p>
      </div>

      {/* Text area - spacious, calm */}
      <textarea
        value={teachBackText}
        onChange={(e) => {
          setTeachBackText(e.target.value);
          setIsSaved(false);
        }}
        placeholder="Write in any language. Use your own examples. There are no wrong answers here."
        className="w-full h-40 p-4 bg-slate-900 border border-slate-600 rounded-lg
                   text-slate-200 placeholder-slate-500
                   focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500
                   resize-none"
      />

      {/* Character guide - not a limit, just awareness */}
      <div className="mt-2 text-right">
        <span className="text-slate-500 text-sm">
          {teachBackText.length > 0 ? `${teachBackText.length} characters` : ''}
        </span>
      </div>

      {/* Real-world application prompt - invitation, not requirement */}
      {pillar && (
        <div className="mt-6 p-4 bg-slate-900/30 rounded-lg border border-slate-600/30">
          <p className="text-slate-400 text-sm mb-2">
            Where might you use this this week?
          </p>
          <p className="text-slate-300 text-sm leading-relaxed">
            {pillar.name === 'Problem-Solving Literacy' && "Is there a situation you're facing that feels messy or overwhelming?"}
            {pillar.name === 'Digital Literacy' && "Is there a device, app, or process that now makes more sense?"}
            {pillar.name === 'Economic Literacy' && "Is there a small inefficiency in your day this could reduce?"}
            {pillar.name === 'AI Literacy' && "Where might confidence hide uncertainty?"}
          </p>
          <p className="text-slate-500 text-xs mt-3 italic">
            Just notice. You don't need to write anything down.
          </p>
        </div>
      )}

      {/* Actions - save is primary, skip is subtle */}
      <div className="mt-6 flex items-center justify-between">
        <button
          onClick={handleSkip}
          className="px-4 py-2 text-slate-400 hover:text-slate-300 transition text-sm"
        >
          Skip for now
        </button>

        <button
          onClick={handleSave}
          disabled={teachBackText.trim().length === 0}
          className={`px-6 py-3 rounded-lg font-semibold transition ${
            teachBackText.trim().length === 0
              ? 'bg-slate-700 text-slate-500 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700 text-white'
          }`}
        >
          {isSaved ? 'Saved ✓' : 'Save & Continue'}
        </button>
      </div>

      {/* Privacy reminder - subtle but present */}
      <div className="mt-4 pt-4 border-t border-slate-700/50">
        <p className="text-slate-500 text-xs">
          Your teach-back is stored privately on this device. It will never be shared without your permission.
        </p>
      </div>
    </div>
  );
}

export default TeachBack;
